import React from "react";
import { useParams } from 'react-router-dom'
import { useGetVoiceByCode } from "hooks/api/voices";
import Voice from "components/elements/Voice";
import PanelTitle from "components/elements/PanelTitle";
import LoaderSpinning from "components/elements/LoaderSpinning";
import Button from "components/elements/Button";
import Panel from "components/elements/Panel";
import Empty from "components/elements/Empty";
import { ClipboardCopyIcon } from "@heroicons/react/solid";
import { toast } from "react-toastify";
import { setActiveVoice } from 'hooks/useActiveVoice'
import {useThemeColor} from "hooks/api/themes-color";
import { useLocation } from 'react-router-dom'

const TAXON_TYPES = {
    VOICE: 'voice',
    IDENT: 'ident',
    TALENT: 'talent',
    MUSIC: 'music'
} as const;

interface TaxonSectionProps {
    type: keyof typeof TAXON_TYPES;
    data: any;
    getSubTaxons: (sub: string) => string;
}

const TaxonSection: React.FC<TaxonSectionProps> = ({ type, data, getSubTaxons }) => {
    const taxonType = TAXON_TYPES[type];
    const getMainTaxon = (main: string) => {
        return data.filters.filter(filter =>
            filter.hierarchy[0] && filter.hierarchy[0] === main
        ).map(filter =>
            filter.name
        ).join("; ")
    }

    if (!getMainTaxon(taxonType)) return null;

    const renderFields = () => {
        switch (type) {
            case 'VOICE':
                return (
                    <>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Language</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('voice_language')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Gender</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('voice_gender')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Age</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('voice_age')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Characters</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('voice_character')}
                            </dd>
                        </div>
                    </>
                );
            case 'IDENT':
                return (
                    <>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Genre</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('ident_genre')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Video theme</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('ident_theme')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Mood</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('ident_mood')}
                            </dd>
                        </div>
                    </>
                );
            case 'TALENT':
                return (
                    <>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Genre</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('talent_genre')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Type</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('talent_type')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Instruments</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('talent_instrument')}
                            </dd>
                        </div>
                    </>
                );
            case 'MUSIC':
                return (
                    <>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Genre</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_genre')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Video theme</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_theme')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Length</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_length')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Vocal</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_vocals')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Mood</dt>
                            <dd className="mt-1 text-sm text-gray-900 capitalize sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_mood')}
                            </dd>
                        </div>
                        <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Instruments</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {getSubTaxons('music_instrument')}
                            </dd>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {renderFields()}
            <div className="px-8 py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{data.description}</dd>
            </div>
        </>
    );
};

export default function VoiceShare() {
    const themeId = parseInt(new URLSearchParams(useLocation().search).get('theme') ?? '0')
    const themeColor = useThemeColor(themeId)
    const { data: buttonColor } = themeColor("button-color")
    const { data: buttonTextColor } = themeColor("button-text-color")

    const { code } = useParams()
    const {data, isLoading, error} = useGetVoiceByCode(code)

    const getSubTaxons = (sub: string) => {
        return data.filters.filter(filter =>
            filter.hierarchy[1] && filter.hierarchy[1] === sub
        ).map(filter =>
            filter.name
        ).join("; ")
    }

    const copyLink = () => {
        navigator.clipboard.writeText(`${window.location.origin}/share/explore/${data.code}`).then(() => {
            toast.success("Successfully copied link to your clipboard")
        }).catch(() => {
            toast.error("Something went wrong while trying to copy link to your clipboard")
        })
    }

    return <>
    {isLoading ? <LoaderSpinning className="py-9">Loading...</LoaderSpinning> :
        error ? <Empty>This explore item does not exist</Empty> :
        <>
            <div className="h-auto aspect-w-10 aspect-h-10">
                <Voice voice={data} onClick={() => setActiveVoice( data )}></Voice>
            </div>
            <Panel className="mt-4 pb-0">
                <PanelTitle>{data &&
                    <div className="flex justify-between">
                        <span className="self-center text-black">{data.name}</span>
                        <Button onClick={copyLink} style={{backgroundColor: buttonColor.color, borderColor: buttonColor.color, color: buttonTextColor.color}} >
                            Copy link <ClipboardCopyIcon className="float-right ml-2 h-6"/>
                        </Button>
                    </div>
                }</PanelTitle>
                <div className="-mx-8 mt-8 border-t border-gray-200">
                    <dl className="sm:divide-y sm:divide-gray-200">
                        <TaxonSection type="VOICE" data={data} getSubTaxons={getSubTaxons} />
                        <TaxonSection type="IDENT" data={data} getSubTaxons={getSubTaxons} />
                        <TaxonSection type="TALENT" data={data} getSubTaxons={getSubTaxons} />
                        <TaxonSection type="MUSIC" data={data} getSubTaxons={getSubTaxons} />
                    </dl>
                </div>
            </Panel>
        </>}
    </>
}
